/**
 * Fájl-hozzáférési token a /files útvonalhoz.
 *
 * Korábban a felület MAGA számolta a tokent egy `VITE_SECRET_KEY` nevű
 * „titokból”, ami a publikus JS-kötegbe fordult bele — tehát nem volt titok.
 * Élesen be sem volt állítva, így a token puszta `md5('ÉÉÉÉ-HH-NN-')` volt,
 * amit bárki kiszámolhatott.
 *
 * Mostantól a tokent a SZERVER adja: belépéskor a login válaszában, és
 * bármikor megújítható a /api/files/token végpontról. A felhasználóhoz van
 * kötve és 12 óra alatt lejár.
 *
 * ⚠️ A LEJÁRAT KEZELÉSE A LÉNYEG. Az első változatban csak alkalmazás-
 * indításkor és egy 6 órás `setInterval`-ban újítottunk. A Chrome viszont
 * felfüggeszti a háttérbe tett lapok időzítőit, és alvó gépen sem futnak —
 * így aki (irodában teljesen szokásos módon) nyitva hagyta a rendszert
 * éjszakára, reggel LEJÁRT tokennel kattintott a letöltésre. A szerver
 * ilyenkor `401 {"message":"Invalid token"}`-t ad `application/json`
 * tartalomtípussal, a böngésző pedig ezt menti le fájlként — innen jöttek a
 * „nem tölt le" és a furcsa `.json` fájlok.
 *
 * Ezért három ponton is gondoskodunk a friss tokenről:
 *  1. alkalmazás-indításkor,
 *  2. amikor a lap újra előtérbe kerül (ez fogja meg az éjszakát),
 *  3. közvetlenül a letöltés/megnyitás előtt (`ensureFileToken()`).
 */

const KULCS = "file_token";

/** Ennyi idővel a lejárat ELŐTT már újítunk (perc). */
const ELOREHOZOTT_UJITAS_PERC = 60;

/** Szinkron olvasás — a getStorageUrl() render közben hívja. */
export function getFileToken(): string {
	try {
		return sessionStorage.getItem(KULCS) || "";
	} catch {
		return "";
	}
}

export function setFileToken(token: string): void {
	try {
		if (token) sessionStorage.setItem(KULCS, token);
	} catch {
		// privát mód / letiltott tároló: a képek ilyenkor nem töltenek be,
		// de az alkalmazás működik tovább
	}
}

export function clearFileToken(): void {
	try {
		sessionStorage.removeItem(KULCS);
	} catch {
		// nincs mit tenni
	}
}

/**
 * A token lejárata másodpercben, vagy null ha nem olvasható ki.
 *
 * A token felépítése `base64url("<userId>.<lejárat>.<aláírás>")`. Az aláírást
 * NEM tudjuk (és nem is akarjuk) ellenőrizni a kliensen — csak a lejáratot
 * olvassuk ki, hogy időben tudjunk újítani. A hamisítás ellen a szerver
 * oldali HMAC véd; ide rossz adat legfeljebb egy fölösleges újítást okoz.
 */
export function getFileTokenExpiry(token = getFileToken()): number | null {
	if (!token) return null;

	try {
		const normal = token.replace(/-/g, "+").replace(/_/g, "/");
		const kiegeszitve = normal + "=".repeat((4 - (normal.length % 4)) % 4);
		const reszek = atob(kiegeszitve).split(".");

		if (reszek.length !== 3) return null;

		const lejar = Number.parseInt(reszek[1], 10);

		return Number.isFinite(lejar) ? lejar : null;
	} catch {
		return null;
	}
}

/** Lejárt, vagy hamarosan lejár a mostani token? */
export function fileTokenLejaroban(): boolean {
	const lejar = getFileTokenExpiry();

	// Ha nem tudjuk kiolvasni, inkább újítunk, mint hogy törött URL-t képezzünk.
	if (lejar === null) return true;

	return lejar - ELOREHOZOTT_UJITAS_PERC * 60 <= Math.floor(Date.now() / 1000);
}

/**
 * A bejelentkezett felhasználó API-tokenje.
 *
 * ⚠️ KÉT HELYEN ÉLHET, és ezt a különbséget fizette meg egy felhasználó:
 *
 * A bejelentkezés a `sessionStorage`-ba írja („token"), DE a Zustand
 * userStore a `localStorage`-ba perzisztál (`userStore.ts`:
 * `createJSONStorage(() => localStorage)`). A sessionStorage FÜLENKÉNTI és a
 * böngésző bezárásakor törlődik — a localStorage nem.
 *
 * Ezért egy ÚJ FÜLÖN (vagy böngésző-újraindítás után) a felhasználó még be
 * van léptetve, de a sessionStorage üres. Az első változatom csak onnan
 * olvasott, így a fájl-token megújítása némán elbukott, a felület pedig ÜRES
 * tokennel képezte az URL-t: `/files//price_quotes/...`, amire nem illeszkedik
 * útvonal → `{"message":"Not Found"}`. Pontosan ezt jelentette be egy kolléga
 * az árajánlatok letöltésére 2026-10-13-án, és ezért volt „tegnap jó, ma nem":
 * attól függött, melyik fülön dolgozott.
 *
 * Mostantól ugyanonnan olvasunk, ahonnan az apiClient is (a store), a
 * sessionStorage csak az első, gyorsabb próbálkozás.
 */
function authToken(): string | null {
	try {
		const munkamenetbol = sessionStorage.getItem("token");
		if (munkamenetbol) return munkamenetbol;
	} catch {
		// letiltott tároló: megyünk tovább a localStorage-ra
	}

	// A Zustand persist alakja: { state: { userToken: { accessToken } }, version }
	// Szándékosan NEM importáljuk a store-t, hogy ne keletkezzen körkörös
	// függőség egy ilyen alacsony szintű segédmodulban.
	try {
		const nyers = localStorage.getItem("userStore");
		if (!nyers) return null;

		const ertelmezett = JSON.parse(nyers);
		const token = ertelmezett?.state?.userToken?.accessToken;

		return typeof token === "string" && token ? token : null;
	} catch {
		return null;
	}
}

/** Épp folyamatban lévő kérés, hogy egy lapbetöltésnél ne induljon több. */
let folyamatban: Promise<void> | null = null;

/**
 * Friss tokent kér a szervertől.
 *
 * Némán hibázik: ha nem sikerül, a meglévő token marad érvényben — egy
 * hálózati hiba ne akadályozza meg a munkát.
 */
export async function refreshFileToken(): Promise<void> {
	if (folyamatban) return folyamatban;

	const auth = authToken();
	if (!auth) return;

	folyamatban = (async () => {
		try {
			const valasz = await fetch("/api/files/token", {
				headers: { Accept: "application/json", Authorization: `Bearer ${auth}` },
			});
			if (!valasz.ok) return;
			const eredmeny = await valasz.json();
			const uj = eredmeny?.data?.file_token;
			if (typeof uj === "string" && uj) setFileToken(uj);
		} catch {
			// hálózati hiba: a meglévő token marad
		} finally {
			folyamatban = null;
		}
	})();

	return folyamatban;
}

/**
 * Gondoskodik róla, hogy LEGYEN érvényes token, és visszaadja.
 *
 * Ezt kell hívni minden olyan művelet előtt, ami fájlt nyit meg vagy tölt le:
 * a kattintás-kezelő aszinkron lehet, tehát itt meg tudjuk várni az újítást.
 * Enélkül egy régóta nyitva hagyott lapon a letöltés 401-es JSON-t mentett le
 * fájl helyett.
 */
export async function ensureFileToken(): Promise<string> {
	if (fileTokenLejaroban()) {
		await refreshFileToken();
	}

	return getFileToken();
}
